/* eslint-disable import/no-duplicates */
import React, { useCallback, useRef, useState, useEffect } from 'react';
import {
  Text,
  Button,
  Icon,
  useDisclosure,
  Tooltip,
  ModalBody,
  ModalFooter,
  Box,
  VStack,
  HStack,
  Badge,
  IconButton,
  Divider,
  Flex,
  Switch,
} from '@chakra-ui/react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import { RiPencilLine, RiDeleteBinLine, RiAddLine } from 'react-icons/ri';
import { useLoading, Oval } from '@agney/react-loading';
import { v4 as uuid } from 'uuid';
import { apllyToast } from '../../../Global/Toast2.0';
import getValidationErrors from '../../../../utils/getValidationErrors';
import { roundKm } from '../../../../utils/roundKm';
import {
  isValidReportDate,
  maxReportDate,
  MIN_REPORT_DATE,
} from '../../../../utils/formatReportDate';

import Input from '../../../Global/Input';
import TextArea from '../../../Global/TextArea';
import Select from '../../../Global/SelectRelease';
import { UserSelect } from '../../../Global/UserSelect';
import { UploadSimulated } from '../../../Global/UploadSimulated';
import { ExpenseParticipants } from '../../Miles/ExpenseParticipants';
import {
  ExpenseRateio,
  computeRateioDistributed,
  IRateioItem,
} from '../../Miles/ExpenseRateio';
import { SearchableSelect } from '../../Miles/SearchableSelect';
import { COST_CENTERS } from '../../../../constants/orgStructure';
import { ModalComponent } from '..';

import {
  IExpenseReport,
  IExpenseItem,
  IExpenseParticipant,
  IExpenseRateio,
  isDirectorSector,
  isFixedRateEligibleUser,
  isFixedRateKmObservation,
  recalculateKmGroupItems,
  SPECIAL_SECTOR_RATE,
} from '../../../../pages/Dashboard/vehicles_management/Expenses';
import { usersService, IUserOption, IBranchOption } from '../../../../services/usersService';

// Reconstrói os participantes a partir de um relatório existente,
// caindo nos campos legados quando o relatório ainda não tem participantes.
function participantsFromExpense(
  expense: IExpenseReport,
): IExpenseParticipant[] {
  if (expense.participants && expense.participants.length > 0) {
    return expense.participants.map(p => ({
      ...p,
      id: p.id || uuid(),
      payment_required:
        p.payment_required != null ? p.payment_required : true,
    }));
  }
  return [
    {
      id: uuid(),
      user_id: expense.director,
      role: 'responsible',
      sector: expense.sector || '',
      section: expense.section || '',
      cost_center: expense.cost_center || '',
      branch_id: expense.branches_id || '',
      payment_required: true,
      pix_key: expense.pix_key || '',
      bank_name: expense.bank_name || '',
      bank_agency: expense.bank_agency || '',
      bank_account: expense.bank_account || '',
    },
  ];
}

// Reconstrói o mapa item_id -> cost_center a partir de um rateio no modo
// 'itens', permitindo pré-preencher a opção de CC por despesa na edição.
function itemCostCentersFromRateio(
  rateio?: IExpenseRateio,
): Record<string, string> {
  const map: Record<string, string> = {};
  if (rateio?.mode === 'itens') {
    rateio.allocations.forEach(a => {
      (a.item_ids || []).forEach(itemId => {
        if (a.cost_center) {
          map[itemId] = a.cost_center;
        }
      });
    });
  }
  return map;
}

interface IModalProps {
  expense: IExpenseReport;
  handleEditExpense: (id: string, data: Partial<IExpenseReport>) => void;
}

const reportSchema = Yup.object().shape({
  date: Yup.string()
    .required('Data é obrigatória')
    .test('data-valida', 'Informe uma data válida', value =>
      isValidReportDate(value),
    ),
});

const getItemValidationSchema = (expenseType: string) => {
  const baseSchema: any = {
    expense_type: Yup.string().required('Tipo de despesa é obrigatório'),
  };

  switch (expenseType) {
    case 'Quilometragem adicional':
      return Yup.object().shape({
        ...baseSchema,
        additional_km: Yup.number()
          .required('Quilometragem adicional é obrigatória')
          .min(1, 'Quilometragem deve ser no mínimo 1 km')
          .max(1000, 'Quilometragem deve ser no máximo 1000 km')
          .integer('Quilometragem deve ser um número inteiro'),
        displacement_reason: Yup.string()
          .required('Motivo do deslocamento é obrigatório')
          .min(10, 'Descreva o motivo com pelo menos 10 caracteres')
          .max(500, 'Máximo de 500 caracteres'),
      });

    case 'Refeição':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero')
          .test(
            'max-meal-amount',
            'Valor da refeição acima do sugerido (R$ 70,00)',
            value => value === undefined || value <= 70,
          ),
        receipt: Yup.object().required('Comprovante é obrigatório'),
      });

    case 'Pedágio adicional':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.object().required('Comprovante é obrigatório'),
      });

    case 'Outros':
      return Yup.object().shape({
        ...baseSchema,
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.object().required('Comprovante é obrigatório'),
        observations: Yup.string()
          .required(
            'Observações são obrigatórias para despesas do tipo "Outros"',
          )
          .min(20, 'Descreva a despesa com pelo menos 20 caracteres')
          .max(1000, 'Máximo de 1000 caracteres'),
      });

    case 'Ajuda de custo':
      return Yup.object().shape({
        expense_type: Yup.string().required('Tipo de despesa é obrigatório'),
        amount: Yup.number()
          .required('Valor é obrigatório')
          .min(0.01, 'Valor deve ser maior que zero'),
        receipt: Yup.mixed().required('Comprovante é obrigatório'),
        observations: Yup.string().required('Observação é obrigatória'),
      });

    default:
      return Yup.object().shape(baseSchema);
  }
};

const getExpenseColor = (type: string): string => {
  if (type === 'Refeição') return 'green';
  if (type === 'Quilometragem adicional') return 'blue';
  if (type === 'Pedágio adicional') return 'orange';
  if (type === 'Ajuda de custo') return 'teal';
  return 'purple';
};

export function ModalEditExpense({
  expense,
  handleEditExpense,
}: IModalProps) {
  const formRef = useRef<FormHandles>(null);
  const itemFormRef = useRef<FormHandles>(null);
  // Rolagem automática: até o formulário ao começar a editar, e de volta pro
  // item ao terminar (salvar/cancelar) — sem isso, numa lista longa, editar
  // um item lá em cima e ter o formulário aparecer lá embaixo desorienta.
  const itemFormBoxRef = useRef<HTMLDivElement>(null);
  const itemRowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const { onOpen, isOpen, onClose } = useDisclosure();

  const componentRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);

  const [managerId, setManagerId] = useState(expense.manager_id || '');
  const [users, setUsers] = useState<IUserOption[]>([]);

  const [branches, setBranches] = useState<IBranchOption[]>([]);

  const [participants, setParticipants] = useState<IExpenseParticipant[]>(
    participantsFromExpense(expense),
  );
  const [rateio, setRateio] = useState<IExpenseRateio>(
    expense.rateio || { mode: 'percentual', allocations: [] },
  );

  // Centro de custo POR despesa (conveniência que alimenta o rateio modo 'itens')
  const [costCenterPerItem, setCostCenterPerItem] = useState(
    expense.rateio?.mode === 'itens',
  );
  const [itemCostCenters, setItemCostCenters] = useState<
    Record<string, string>
  >(() => itemCostCentersFromRateio(expense.rateio));
  const costCenterOptions = COST_CENTERS.map(c => ({
    value: c.code,
    label: `${c.code} – ${c.name}`,
  }));

  const handleTogglePerItem = useCallback((checked: boolean) => {
    setCostCenterPerItem(checked);
    if (!checked) {
      setItemCostCenters({});
    }
  }, []);

  const handleItemCostCenterChange = useCallback(
    (itemId: string, value: string) => {
      setItemCostCenters(prev => ({ ...prev, [itemId]: value }));
    },
    [],
  );

  const responsible =
    participants.find(p => p.role === 'responsible') || participants[0];
    const selectedUserSectorId = users.find(u => u.id === responsible?.user_id)?.sector_id;
  const isDirectorUser = isDirectorSector(selectedUserSectorId);
  // Diretoria ou usuário na lista de taxa fixa por ID (ver kmRateTable.ts)
  // pode escolher a tabela fixa de R$ 1,63/km ao editar um item de KM.
  const canUseFixedKmRate =
    isDirectorUser || isFixedRateEligibleUser(responsible?.user_id);

  const [items, setItems] = useState<IExpenseItem[]>(expense.items);

  // Item sendo editado no formulário abaixo (null = modo "adicionar novo").
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [useDirectorKmRate, setUseDirectorKmRate] = useState(false);

  // Carregar usuários e filiais ao abrir
  useEffect(() => {
    if (isOpen) {
      usersService
        .list()
        .then(data => setUsers(data))
        .catch(err => console.error('Erro ao carregar usuários:', err));
      usersService
        .listBranches()
        .then(data => setBranches(data))
        .catch(err => console.error('Erro ao carregar filiais:', err));
    }
  }, [isOpen]);
  const [currentItemType, setCurrentItemType] = useState('');

  const [showAmountField, setShowAmountField] = useState(false);
  const [showKmFields, setShowKmFields] = useState(false);
  const [showReceiptField, setShowReceiptField] = useState(false);
  const [showObservationsField, setShowObservationsField] = useState(false);
  const [showAjudaCustoFields, setShowAjudaCustoFields] = useState(false);
  const [eventSchedules, setEventSchedules] = useState<{ id: string }[]>([{ id: uuid() }]);

  // Resetar estado ao abrir o modal
  useEffect(() => {
    if (isOpen) {
      setManagerId(expense.manager_id || '');
      setParticipants(participantsFromExpense(expense));
      setRateio(expense.rateio || { mode: 'percentual', allocations: [] });
      setCostCenterPerItem(expense.rateio?.mode === 'itens');
      setItemCostCenters(itemCostCentersFromRateio(expense.rateio));
      setItems(expense.items);
      setCurrentItemType('');
      setEditingItemId(null);
      setUseDirectorKmRate(false);

      formRef.current?.setData({ date: expense.date });
    }
  }, [isOpen, expense]);

  useEffect(() => {
    switch (currentItemType) {
      case 'Ajuda de custo':
        setShowKmFields(false);
        setShowAmountField(true);
        setShowReceiptField(true);
        setShowObservationsField(false);
        setShowAjudaCustoFields(true);
        break;

      case 'Quilometragem adicional':
        setShowKmFields(true);
        setShowAmountField(false);
        setShowReceiptField(false);
        setShowObservationsField(false);
        setShowAjudaCustoFields(false);
        break;

      case 'Refeição':
      case 'Pedágio adicional':
        setShowKmFields(false);
        setShowAmountField(true);
        setShowReceiptField(true);
        setShowObservationsField(false);
        setShowAjudaCustoFields(false);
        break;

      case 'Outros':
        setShowKmFields(false);
        setShowAmountField(true);
        setShowReceiptField(true);
        setShowObservationsField(true);
        setShowAjudaCustoFields(false);
        break;

      default:
        setShowKmFields(false);
        setShowAmountField(false);
        setShowReceiptField(false);
        setShowObservationsField(false);
        setShowAjudaCustoFields(false);
    }
  }, [currentItemType]);

  const handleItemTypeChange = useCallback((value: string) => {
    if (value === 'Ajuda de custo' && items.length > 0) {
      apllyToast('warning', 'Ajuda de custo deve ser a única despesa adicionada neste relatório.');
      return;
    }
    if (value !== 'Ajuda de custo' && items.some(i => i.expense_type === 'Ajuda de custo')) {
      apllyToast('warning', 'Uma Ajuda de custo não pode ser combinada com outras despesas neste relatório.');
      return;
    }
    setCurrentItemType(value);
    setUseDirectorKmRate(false);
    itemFormRef.current?.setFieldValue('item_amount', '');
    itemFormRef.current?.setFieldValue('item_additional_km', '');
    itemFormRef.current?.setFieldValue('item_displacement_reason', '');
    itemFormRef.current?.setFieldValue('item_receipt', null);
    itemFormRef.current?.setFieldValue('item_observations', '');
    setEventSchedules([{ id: uuid() }]);
  }, [items]);

  // Preenche o formulário com os dados de um item já lançado, pra corrigir
  // sem precisar excluir e relançar. Ajuda de custo fica de fora por ora —
  // tem uma estrutura própria bem maior (dados do evento, horários, viagem).
  const handleStartEditItem = useCallback((item: IExpenseItem) => {
    setEditingItemId(item.id);
    setCurrentItemType(item.expense_type);
    setUseDirectorKmRate(
      item.expense_type === 'Quilometragem adicional'
        ? isFixedRateKmObservation(item.observations)
        : false,
    );

    // O campo só é desenhado depois que o tipo muda (showKmFields/
    // showAmountField reagem via useEffect, um ciclo de render depois) — sem
    // o setTimeout, o preenchimento tenta acontecer num campo que ainda não
    // existe na tela e o valor simplesmente some (aparece "vazio").
    setTimeout(() => {
      if (item.expense_type === 'Quilometragem adicional') {
        itemFormRef.current?.setFieldValue(
          'item_additional_km',
          String(item.additional_km || ''),
        );
        itemFormRef.current?.setFieldValue(
          'item_displacement_reason',
          item.displacement_reason || '',
        );
      } else {
        itemFormRef.current?.setFieldValue(
          'item_amount',
          String(item.amount || ''),
        );
        itemFormRef.current?.setFieldValue(
          'item_observations',
          item.expense_type === 'Outros' ? item.observations || '' : '',
        );
      }
    }, 0);

    itemFormBoxRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }, []);

  // Sai do modo de edição sem salvar — o item original permanece intacto.
  const handleCancelEditItem = useCallback(() => {
    const previousItemId = editingItemId;
    setEditingItemId(null);
    setCurrentItemType('');
    setUseDirectorKmRate(false);
    itemFormRef.current?.reset();
    setEventSchedules([{ id: uuid() }]);
    if (previousItemId) {
      itemRowRefs.current[previousItemId]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [editingItemId]);

  const handleSaveItem = useCallback(
    async (data: any) => {
      try {
        itemFormRef.current?.setErrors({});

        const originalItem = editingItemId
          ? items.find(it => it.id === editingItemId)
          : undefined;

        let finalObservations = data.item_observations;
        if (currentItemType === 'Ajuda de custo') {
          const ajudaData = {
            nome: data.ajuda_custo_nome,
            objetivo: data.ajuda_custo_objetivo,
            local_evento: data.ajuda_custo_local,
            endereco: data.ajuda_custo_endereco,
            periodo_evento: data.ajuda_custo_periodo,
            horarios: eventSchedules.map((_, index) => ({
              inicio: data[`ajuda_custo_horario_inicio_${index}`],
              termino: data[`ajuda_custo_horario_termino_${index}`]
            })).filter(h => h.inicio || h.termino),
            dados_viagem_data: data.ajuda_custo_data_viagem,
            trecho_ida: data.ajuda_custo_trecho_ida,
            trecho_ida_data: data.ajuda_custo_trecho_ida_data,
            trecho_volta: data.ajuda_custo_trecho_volta,
            trecho_volta_data: data.ajuda_custo_trecho_volta_data
          };
          finalObservations = JSON.stringify(ajudaData);
        }

        // Ao editar, se o usuário não escolher um arquivo novo, mantém o
        // comprovante que o item já tinha — sem isso, corrigir só o valor de
        // uma despesa já exigiria reanexar o comprovante de novo.
        const effectiveReceipt = data.item_receipt || originalItem?.receipt;

        const schema = getItemValidationSchema(currentItemType);
        await schema.validate(
          {
            expense_type: currentItemType,
            amount: data.item_amount,
            additional_km: data.item_additional_km,
            displacement_reason: data.item_displacement_reason,
            receipt: effectiveReceipt,
            observations: finalObservations,
          },
          { abortEarly: false },
        );

        const savedItem: IExpenseItem = {
          id: editingItemId || uuid(),
          expense_type: currentItemType as IExpenseItem['expense_type'],
        };

        if (currentItemType === 'Quilometragem adicional') {
          // Coluna `integer` no banco — decimal aqui derrubava a gravação
          // (ver roundKm).
          const km = roundKm(data.item_additional_km);
          savedItem.additional_km = km;
          savedItem.displacement_reason = data.item_displacement_reason;

          // Esta tela não tem campo próprio de observação livre para KM —
          // preserva o texto que já existia no item (obs), se houver.
          let existingObs = '';
          try {
            const parsed = JSON.parse(originalItem?.observations || '{}');
            existingObs = parsed.obs || '';
          } catch (e) {
            // observações antigas em texto puro: ignora, não há "obs" a preservar
          }

          if (useDirectorKmRate && canUseFixedKmRate) {
            savedItem.amount = Number((km * SPECIAL_SECTOR_RATE).toFixed(2));
            savedItem.observations = JSON.stringify({
              km_rate: SPECIAL_SECTOR_RATE,
              km_rate_type: isDirectorUser ? 'diretoria' : 'fixo',
              obs: existingObs,
            });
          } else {
            // Sem marca de taxa fixa: o recalculateKmGroupItems (chamado
            // logo abaixo) calcula amount e km_rate pela faixa do grupo.
            savedItem.observations = JSON.stringify({ obs: existingObs });
          }
        } else {
          savedItem.amount = Number(data.item_amount);
          savedItem.receipt = effectiveReceipt;

          if (currentItemType === 'Outros' || currentItemType === 'Ajuda de custo') {
            savedItem.observations = finalObservations;
          }
        }

        setItems(prev => {
          const next = editingItemId
            ? prev.map(it => (it.id === editingItemId ? savedItem : it))
            : [...prev, savedItem];
          return recalculateKmGroupItems(next, selectedUserSectorId);
        });
        apllyToast('success', editingItemId ? 'Item atualizado!' : 'Item adicionado!');

        if (editingItemId) {
          const editedItemId = editingItemId;
          setTimeout(() => {
            itemRowRefs.current[editedItemId]?.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            });
          }, 0);
        }

        setEditingItemId(null);
        setCurrentItemType('');
        setUseDirectorKmRate(false);
        itemFormRef.current?.reset();
        setEventSchedules([{ id: uuid() }]);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          itemFormRef.current?.setErrors(errors);
        } else {
          apllyToast('warning', 'Erro ao salvar item!');
        }
      }
    },
    [
      currentItemType,
      eventSchedules,
      editingItemId,
      items,
      useDirectorKmRate,
      canUseFixedKmRate,
      isDirectorUser,
      selectedUserSectorId,
    ],
  );

  const handleRemoveItem = useCallback(
    (id: string) => {
      setItems(prev =>
        recalculateKmGroupItems(
          prev.filter(item => item.id !== id),
          selectedUserSectorId,
        ),
      );
      setItemCostCenters(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      apllyToast('info', 'Item removido!');
    },
    [selectedUserSectorId],
  );

  // Valor de um item (cai na taxa de KM quando o amount não está definido)
  const itemValue = (i: IExpenseItem): number => {
    if (i.amount != null) return i.amount;
    if (i.expense_type === 'Quilometragem adicional' && i.additional_km) {
      let rate = 1.63;
      try {
        const parsed = JSON.parse(i.observations || '');
        if (parsed.km_rate) rate = parsed.km_rate;
      } catch (e) {
        // observações não-JSON: mantém taxa padrão
      }
      return Number((i.additional_km * rate).toFixed(2));
    }
    return 0;
  };

  const expensesTotal = items.reduce((sum, i) => sum + itemValue(i), 0);
  const rateioItems: IRateioItem[] = items.map(i => ({
    id: i.id,
    label:
      i.expense_type === 'Quilometragem adicional'
        ? `${i.expense_type} (${i.additional_km || 0} km)`
        : i.expense_type,
    value: itemValue(i),
  }));

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        formRef.current?.setErrors({});

        await reportSchema.validate(data, { abortEarly: false });

        if (!responsible || !responsible.user_id) {
          apllyToast('warning', 'Selecione o usuário responsável!');
          setLoading(false);
          return;
        }

        const incomplete = participants.find(
          p =>
            !p.user_id ||
            !p.sector?.trim() ||
            !p.section?.trim() ||
            !p.cost_center?.trim(),
        );
        if (incomplete) {
          apllyToast(
            'warning',
            'Preencha usuário, setor, seção e centro de custo de todos os participantes!',
          );
          setLoading(false);
          return;
        }

        if (items.length === 0) {
          apllyToast(
            'warning',
            'Adicione pelo menos uma despesa ao relatório!',
          );
          setLoading(false);
          return;
        }

        if (!isDirectorUser && !managerId) {
          apllyToast('warning', 'Selecione o gestor responsável!');
          setLoading(false);
          return;
        }

        if (rateio.allocations.length > 0) {
          const distributed = computeRateioDistributed(
            rateio,
            expensesTotal,
            rateioItems,
          );
          if (distributed > expensesTotal + 0.001) {
            apllyToast(
              'warning',
              'O rateio ultrapassa o total das despesas. Ajuste as alocações.',
            );
            setLoading(false);
            return;
          }
        }

        // Se o usuário optou por CC por despesa, monta rateio modo 'itens'
        // agrupando itens por centro de custo.
        let effectiveRateio =
          rateio.allocations.length > 0 ? rateio : undefined;
        if (costCenterPerItem) {
          const missing = items.some(it => !itemCostCenters[it.id]);
          if (missing) {
            apllyToast(
              'warning',
              'Defina o centro de custo de todas as despesas ou desative a opção por despesa.',
            );
            setLoading(false);
            return;
          }
          const byCC = new Map<string, string[]>(); // cost_center -> item_ids
          items.forEach(it => {
            const cc = itemCostCenters[it.id];
            if (cc) {
              const arr = byCC.get(cc) || [];
              arr.push(it.id);
              byCC.set(cc, arr);
            }
          });
          if (byCC.size > 0) {
            effectiveRateio = {
              mode: 'itens',
              allocations: Array.from(byCC.entries()).map(
                ([cost_center, item_ids]) => ({
                  id: uuid(),
                  cost_center,
                  item_ids,
                }),
              ),
            };
          }
        }

        const updatedData: Partial<IExpenseReport> = {
          director: responsible.user_id,
          manager_id: managerId,
          branches_id: responsible.branch_id || undefined,
          date: data.date,
          sector: responsible.sector,
          section: responsible.section,
          // Com CC por despesa, o rateio é a fonte de verdade — o CC único do
          // relatório vira um marcador ('MÚLTIPLOS') para o lançamento contábil
          // não receber um centro de custo conflitante.
          cost_center: costCenterPerItem ? 'MÚLTIPLOS' : responsible.cost_center,
          pix_key: responsible.pix_key || undefined,
          bank_name: responsible.bank_name || undefined,
          bank_agency: responsible.bank_agency || undefined,
          bank_account: responsible.bank_account || undefined,
          participants,
          rateio: effectiveRateio,
          items,
        };

        handleEditExpense(expense.id, updatedData);
        onClose();
        setLoading(false);
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else {
          apllyToast('warning', 'Problemas ao atualizar relatório!');
        }
        setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [responsible, participants, managerId, rateio, rateioItems, expensesTotal, items, costCenterPerItem, itemCostCenters, expense.id, handleEditExpense, onClose],
  );

  const { containerProps, indicatorEl } = useLoading({
    loading,
    indicator: <Oval />,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const getSignatureStatusText = () => {
    if (expense.signatureStatus === 'sent') {
      return 'Enviado para assinatura digital';
    }
    if (expense.signatureStatus === 'rejected') {
      return 'Assinatura rejeitada';
    }
    return 'Aguardando envio para assinatura';
  };

  return (
    <>

      <Tooltip hasArrow label="Editar">
        <Button
          size="sm"
          colorScheme="blue"
          variant="ghost"
          onClick={onOpen}
        >
          <Icon as={RiPencilLine} fontSize="20" />
        </Button>
      </Tooltip>

      <ModalComponent
        title="Editar Relatório de Despesas e Reembolsos"
        isOpen={isOpen}
        onClose={onClose}
        sizeSpecifyc="4xl"
      >
        <ModalBody pb={6} maxH="80vh" overflowY="auto" color="#1a202c">
          <Form
            ref={formRef}
            onSubmit={handleSubmit}
            initialData={{ date: expense.date }}
          >
            <Box bg="blue.50" p={4} borderRadius="md" mb={4} color="#1a202c">
              <Text fontWeight="bold" mb={3} color="blue.700">
                Dados do Relatório
              </Text>

              <Box data-field="participants" mb={4}>
                <Text fontSize="sm" fontWeight="600" mb={1}>
                  Participantes (Responsável e Vinculados)
                </Text>
                <Text fontSize="xs" color="#64748b" mb={3}>
                  Cada participante possui seu próprio setor, seção, centro de
                  custo e filial. O pagamento de cada vinculado é opcional.
                </Text>
                <ExpenseParticipants
                  participants={participants}
                  onChange={setParticipants}
                  users={users}
                  branches={branches}
                />
              </Box>

              <Box mt={4} data-field="manager">
                <HStack spacing={2} mb={2}>
                  <Text fontSize="sm" fontWeight="600">
                    Gestor Responsável (aprovação)
                  </Text>
                </HStack>
                <UserSelect
                  users={users.filter(
                    u => !participants.some(p => p.user_id === u.id),
                  )}
                  value={managerId}
                  onChange={setManagerId}
                  placeholder="Pesquisar e selecionar gestor"
                />
              </Box>

              <p>Data do gasto</p>
              <Input
                type="date"
                name="date"
                min={MIN_REPORT_DATE}
                max={maxReportDate()}
              />

              <Box mt={4}>
                <Text fontSize="sm" fontWeight="600" mb={2}>
                  Centro de Custo
                </Text>
                <HStack
                  p={3}
                  bg="gray.100"
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor="blue.500"
                  spacing={3}
                  align="flex-start"
                >
                  <Switch
                    size="sm"
                    mt="2px"
                    isChecked={costCenterPerItem}
                    onChange={e => handleTogglePerItem(e.target.checked)}
                    colorScheme="blue"
                  />
                  <Box>
                    <Text fontSize="sm" fontWeight="600" color="gray.700">
                      Definir centro de custo por despesa
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      Ativado: cada despesa terá seu próprio centro de custo.
                      Desativado: usa o centro de custo do responsável para todo o
                      relatório.
                    </Text>
                  </Box>
                </HStack>
              </Box>

              {expense.signature && (
                <Box mt={3} p={3} bg="green.50" borderRadius="md">
                  <Text fontSize="sm" color="green.700">
                    <strong>Assinado por:</strong> {expense.signature.name}
                    {expense.signedAt && ` em ${new Date(expense.signedAt).toLocaleDateString('pt-BR')}`}
                  </Text>
                </Box>
              )}

              {!expense.signature && (
                <Box mt={3} p={3} bg="blue.50" borderRadius="md">
                  <Text fontSize="sm" color="blue.700">
                    <strong>Status:</strong> {getSignatureStatusText()}
                  </Text>
                </Box>
              )}
            </Box>

            <Divider my={4} />

            <Box mb={4}>
              <Flex justify="space-between" align="center" mb={3}>
                <Text fontWeight="bold" color="gray.700">
                  Despesas no Relatório ({items.length})
                </Text>
              </Flex>

              {costCenterPerItem && (
                <HStack
                  mb={3}
                  p={3}
                  bg="blue.50"
                  borderRadius="md"
                  borderLeft="3px solid"
                  borderLeftColor="blue.500"
                  spacing={3}
                >
                  <Text fontSize="xs" color="gray.700">
                    Centro de custo por despesa ativado — defina o centro de
                    custo de cada item abaixo.
                  </Text>
                </HStack>
              )}

              {items.length === 0 ? (
                <Box
                  p={4}
                  bg="gray.50"
                  borderRadius="md"
                  textAlign="center"
                  color="gray.500"
                >
                  Nenhuma despesa adicionada ainda
                </Box>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {items.map(item => (
                    <Box
                      key={item.id}
                      ref={el => {
                        itemRowRefs.current[item.id] = el;
                      }}
                      p={3}
                      bg={item.id === editingItemId ? 'blue.50' : 'white'}
                      borderWidth={1}
                      borderColor={item.id === editingItemId ? 'blue.300' : 'gray.200'}
                      borderRadius="md"
                    >
                      <HStack justify="space-between" spacing={3}>
                        <HStack spacing={3} flex={1} minWidth={0}>
                          <Badge
                            colorScheme={getExpenseColor(item.expense_type)}
                            flexShrink={0}
                          >
                            {item.expense_type}
                          </Badge>
                          <Text fontSize="sm" flexShrink={0}>
                            {item.expense_type === 'Quilometragem adicional'
                            ? `${item.additional_km} km`
                            : formatCurrency(item.amount || 0)}
                          </Text>
                          {item.displacement_reason && (
                          <Text fontSize="xs" color="gray.500" isTruncated flex={1} minWidth={0}>
                            {item.displacement_reason}
                          </Text>
                        )}
                          {item.observations && (
                          <Text fontSize="xs" color="gray.500" isTruncated flex={1} minWidth={0}>
                            {(() => {
                              if (item.expense_type === 'Ajuda de custo') {
                                try {
                                  const parsed = JSON.parse(item.observations);
                                  return `Evento: ${parsed.nome || 'N/A'}`;
                                } catch(e) {
                                  return item.observations;
                                }
                              }
                              if (item.expense_type === 'Quilometragem adicional') {
                                try {
                                  const parsed = JSON.parse(item.observations);
                                  return parsed.obs ? `Obs: ${parsed.obs}` : '';
                                } catch(e) {
                                  return item.observations;
                                }
                              }
                              return item.observations;
                            })()}
                          </Text>
                        )}
                        </HStack>
                        <HStack spacing={1} flexShrink={0}>
                          {item.expense_type !== 'Ajuda de custo' && (
                            <IconButton
                              aria-label="Editar item"
                              icon={<RiPencilLine />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleStartEditItem(item)}
                            />
                          )}
                          <IconButton
                            aria-label="Remover item"
                            icon={<RiDeleteBinLine />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => handleRemoveItem(item.id)}
                          />
                        </HStack>
                      </HStack>
                      {costCenterPerItem && (
                        <Box mt={2}>
                          <Text fontSize="xs" color="gray.500" mb={1}>
                            Centro de custo desta despesa
                          </Text>
                          <SearchableSelect
                            options={costCenterOptions}
                            value={itemCostCenters[item.id] || ''}
                            onChange={value =>
                              handleItemCostCenterChange(item.id, value)}
                            placeholder="Centro de custo desta despesa"
                            emptyMessage="Nenhum centro de custo"
                          />
                        </Box>
                      )}
                    </Box>
                  ))}
                </VStack>
              )}
            </Box>

            <Divider my={4} />

            <Form ref={itemFormRef} onSubmit={handleSaveItem}>
              <Box ref={itemFormBoxRef} bg="green.50" p={4} borderRadius="md" mb={4}>
                <Text fontWeight="bold" mb={3} color="green.700">
                  {editingItemId ? 'Editar Despesa' : 'Adicionar Nova Despesa'}
                </Text>

                <Select
                  name="item_expense_type"
                  label="Tipo de Despesa"
                  value={currentItemType}
                  onChange={e => handleItemTypeChange(e.target.value)}
                  isDisabled={!!editingItemId}
                  options={[
                    { value: 'Refeição', label: 'Refeição' },
                    {
                      value: 'Quilometragem adicional',
                      label: 'Quilometragem adicional',
                    },
                    {
                      value: 'Pedágio adicional',
                      label: 'Pedágio adicional',
                    },
                    { value: 'Ajuda de custo', label: 'Ajuda de custo' },
                    { value: 'Outros', label: 'Outros' },
                  ]}
                />
                {editingItemId && (
                  <Text fontSize="xs" color="gray.500" mt={1} mb={2}>
                    Para mudar o tipo, exclua esta despesa e adicione uma nova.
                  </Text>
                )}

                {showAmountField && (
                  <>
                    <p>Valor (R$)</p>
                    <Input
                      type="number"
                      step="0.01"
                      name="item_amount"
                      placeholder="Ex: 45.90"
                    />
                    {currentItemType === 'Refeição' && (
                      <Text fontSize="xs" color="gray.500" mt="-2" mb={2}>
                        Valor sugerido até R$ 70,00
                      </Text>
                    )}
                  </>
                )}

                {showKmFields && (
                  <>
                    <p>Quilometragem Adicional (km)</p>
                    <Input
                      type="number"
                      step="1"
                      name="item_additional_km"
                      placeholder="Ex: 150"
                    />

                    {canUseFixedKmRate && (
                      <HStack
                        mt={2}
                        mb={3}
                        p={2}
                        bg="blue.50"
                        borderRadius="md"
                        borderLeft="3px solid"
                        borderLeftColor="blue.400"
                        spacing={3}
                      >
                        <Switch
                          size="sm"
                          isChecked={useDirectorKmRate}
                          onChange={e => setUseDirectorKmRate(e.target.checked)}
                          colorScheme="blue"
                        />
                        <Text fontSize="xs" color="gray.700">
                          {useDirectorKmRate
                            ? `Tabela Fixa (R$ ${SPECIAL_SECTOR_RATE.toFixed(2)}/km)`
                            : 'Tabela de KM padrão (por faixa)'}
                        </Text>
                      </HStack>
                    )}

                    <p>Motivo do Deslocamento</p>
                    <TextArea
                      name="item_displacement_reason"
                      placeholder="Descreva o motivo do deslocamento adicional..."
                    />
                  </>
                )}

                {showReceiptField && (
                  <>
                    <p>Comprovante</p>
                    <UploadSimulated name="item_receipt" />
                  </>
                )}

                {showObservationsField && (
                  <>
                    <p>Observações</p>
                    <TextArea
                      name="item_observations"
                      placeholder="Descreva detalhadamente a despesa..."
                    />
                  </>
                )}

                {showAjudaCustoFields && (
                  <Box
                    p={4}
                    bg="white"
                    borderRadius="md"
                    borderLeft="3px solid"
                    borderColor="teal.500"
                    boxShadow="sm"
                    mt={4}
                  >
                    <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>
                      DADOS DO EVENTO
                    </Text>

                    <VStack spacing={3} align="stretch" mb={4}>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Nome:</Text>
                        <Input name="ajuda_custo_nome" placeholder="Ex: Conferência Anual" />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Objetivo:</Text>
                        <Input name="ajuda_custo_objetivo" placeholder="Ex: Networking e parcerias" />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Local do evento:</Text>
                        <Input name="ajuda_custo_local" placeholder="Ex: Centro de Convenções" />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Endereço:</Text>
                        <Input name="ajuda_custo_endereco" placeholder="Ex: Av. Paulista, 1000" />
                      </Box>
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Período do evento (Início e Fim):</Text>
                        <Input name="ajuda_custo_periodo" placeholder="Ex: Sexta-feira (13/03) a sábado (14/03)" />
                      </Box>

                      <Text fontSize="xs" fontWeight="600" mt={2} mb={1}>Horários do Evento:</Text>
                      {eventSchedules.map((schedule, index) => (
                        <HStack spacing={4} key={schedule.id} align="flex-end">
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Horário de Início:</Text>
                            <Input name={`ajuda_custo_horario_inicio_${index}`} placeholder="Ex: 08h" />
                          </Box>
                          <Box flex={1}>
                            <Text fontSize="xs" fontWeight="600" mb={1}>Horário de Término:</Text>
                            <Input name={`ajuda_custo_horario_termino_${index}`} placeholder="Ex: 17h30" />
                          </Box>
                          {eventSchedules.length > 1 && (
                            <IconButton
                              aria-label="Remover horário"
                              icon={<RiDeleteBinLine />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => setEventSchedules(prev => prev.filter(s => s.id !== schedule.id))}
                            />
                          )}
                        </HStack>
                      ))}
                      <Button
                        size="sm"
                        alignSelf="flex-start"
                        variant="outline"
                        colorScheme="teal"
                        leftIcon={<Icon as={RiAddLine} />}
                        onClick={() => setEventSchedules(prev => [...prev, { id: uuid() }])}
                      >
                        Adicionar Horário
                      </Button>
                    </VStack>

                    <Divider my={4} borderColor="gray.300" />

                    <Text fontSize="sm" fontWeight="bold" color="teal.700" mb={3}>
                      DADOS DA VIAGEM
                    </Text>

                    <VStack spacing={3} align="stretch">
                      <Box>
                        <Text fontSize="xs" fontWeight="600" mb={1}>Data da viagem:</Text>
                        <Input type="date" name="ajuda_custo_data_viagem" />
                      </Box>

                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" mb={1}>Trecho de Ida:</Text>
                          <Input name="ajuda_custo_trecho_ida" placeholder="Ex: SP -> RJ" />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" mb={1}>Data (Ida):</Text>
                          <Input type="date" name="ajuda_custo_trecho_ida_data" />
                        </Box>
                      </HStack>

                      <HStack spacing={4}>
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" mb={1}>Trecho de Volta:</Text>
                          <Input name="ajuda_custo_trecho_volta" placeholder="Ex: RJ -> SP" />
                        </Box>
                        <Box flex={1}>
                          <Text fontSize="xs" fontWeight="600" mb={1}>Data (Volta):</Text>
                          <Input type="date" name="ajuda_custo_trecho_volta_data" />
                        </Box>
                      </HStack>
                    </VStack>
                  </Box>
                )}

                {currentItemType && (
                  <HStack mt={3}>
                    <Button
                      colorScheme="green"
                      size="sm"
                      flex={1}
                      type="button"
                      onClick={() => itemFormRef.current?.submitForm()}
                    >
                      <Icon as={editingItemId ? RiPencilLine : RiAddLine} mr={1} />
                      {editingItemId ? 'Salvar Alterações' : 'Adicionar Item'}
                    </Button>
                    {editingItemId && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={handleCancelEditItem}
                      >
                        Cancelar edição
                      </Button>
                    )}
                  </HStack>
                )}
              </Box>
            </Form>

            {/* Rateio por Centro de Custo */}
            {items.length > 0 && (
              <>
                <Divider my={4} />
                <Box data-field="rateio" mb={4}>
                  <ExpenseRateio
                    rateio={rateio}
                    onChange={setRateio}
                    total={expensesTotal}
                    items={rateioItems}
                    participantCostCenters={participants.map(p => ({
                      cost_center: p.cost_center,
                      sector: p.sector,
                      section: p.section,
                    }))}
                  />
                </Box>
              </>
            )}

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                type="submit"
                isDisabled={items.length === 0}
              >
                {loading ? (
                  <div {...containerProps} ref={componentRef}>
                    {indicatorEl}
                  </div>
                ) : (
                  'Salvar Alterações'
                )}
              </Button>
            </ModalFooter>
          </Form>
        </ModalBody>
      </ModalComponent>
    </>
  );
}
